import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { HttpException } from '../../exceptions/index';
import { ApiService } from '../../services';
import { PageRoute } from '../../types/page.types';
import { buildLoginPathWithRedirect } from '../../utils/auth-redirect.util';
import { useLocale } from '../use-locale.hook';
import { useSelected } from './use-selected';
import { useSetSelected } from './use-set-selected';

const UNAUTHORIZED = 401;

export const useSelectCompanyFromQueryParam = (enabled: boolean) => {
  const navigate = useNavigate();
  const { messagesLocale } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const companyIdFromUrl = searchParams.get('companyId');
  const { selectedCompanyId, isLoading } = useSelected();
  const { setSelected, isPending } = useSetSelected();
  const [isHandled, setIsHandled] = useState(!companyIdFromUrl);
  const didAttemptSelectRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (!companyIdFromUrl) {
      setIsHandled(true);
      return;
    }

    if (isLoading || isPending) {
      return;
    }

    if (selectedCompanyId === companyIdFromUrl) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('companyId');
      setSearchParams(nextParams, { replace: true });
      setIsHandled(true);
      return;
    }

    if (didAttemptSelectRef.current) {
      return;
    }

    didAttemptSelectRef.current = true;

    setSelected(companyIdFromUrl, {
      onSuccess: () => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('companyId');
        setSearchParams(nextParams, { replace: true });
        setIsHandled(true);
      },
      onError: async (error) => {
        // Only a dead session is worth clearing. Every other refusal is this company declining
        // this account — most often a stakeholder row that is not active yet, because the company
        // invitation has not been accepted — and logging the person out over it stranded them on
        // Login with a link that logged them out again on the next try.
        if (error instanceof HttpException && error.response.status !== UNAUTHORIZED) {
          toast.error(messagesLocale['COMPANY_LINK_NO_ACCESS']);
          setIsHandled(true);
          navigate(PageRoute.MY_COMPANIES, { replace: true });

          return;
        }

        const redirectPath = `${window.location.pathname}${window.location.search}`;
        await ApiService.clearStaleAuthSession();
        navigate(buildLoginPathWithRedirect(redirectPath), { replace: true });
      },
    });
  }, [
    companyIdFromUrl,
    enabled,
    isLoading,
    isPending,
    messagesLocale,
    navigate,
    searchParams,
    selectedCompanyId,
    setSearchParams,
    setSelected,
  ]);

  return { isSelectingCompany: enabled && !!companyIdFromUrl && !isHandled };
};
